import axios from 'axios'
import { Reducer, useCallback, useReducer } from 'react'

import useIsMounted from '../../../hooks/useIsMounted'
import { useAuth } from './useAuth'

const auth0Config = {
  domain: process.env.GATSBY_AUTH0_DOMAIN,
  connection: 'Username-Password-Authentication',
}

export interface ChangePasswordResult {
  called: boolean
  data: { email: string } | undefined
  loading: boolean
  error: Error | undefined
}

export type UseChangePassword = [() => void, ChangePasswordResult]

type ChangePasswordReducerAction =
  | { type: 'requestStart' }
  | { type: 'error'; value: Error }
  | { type: 'requestComplete'; value: { email: string } }

const changePasswordReducer: Reducer<ChangePasswordResult, ChangePasswordReducerAction> = (
  prevState,
  action,
) => {
  switch (action.type) {
    case 'requestStart':
      return { called: true, data: undefined, loading: true, error: undefined }
    case 'error':
      return { ...prevState, loading: false, error: action.value }
    case 'requestComplete':
      return { ...prevState, loading: false, data: action.value }
    default:
      return prevState
  }
}

const initialState: ChangePasswordResult = {
  called: false,
  data: undefined,
  loading: false,
  error: undefined,
}

export const useChangePassword = (): UseChangePassword => {
  const [state, dispatch] = useReducer(changePasswordReducer, initialState)
  const isMounted = useIsMounted()
  const { domain, connection } = auth0Config
  const { user } = useAuth()

  const changePassword = useCallback(() => {
    dispatch({ type: 'requestStart' })
    if (!domain) {
      dispatch({ type: 'error', value: new Error(`Auth0 domain not configured`) })
      return
    }
    const email = user?.email
    if (!email) {
      dispatch({ type: 'error', value: new Error(`User email undefined`) })
      return
    }
    axios
      .post(
        `https://${domain}/dbconnections/change_password`,
        {
          email,
          connection,
        },
        { headers: { 'content-type': 'application/json' } },
      )
      .then(() => {
        if (isMounted.current) {
          dispatch({ type: 'requestComplete', value: { email } })
        }
      })
      .catch((err) => {
        if (isMounted.current) {
          dispatch({ type: 'error', value: err instanceof Error ? err : new Error(`${err}`) })
        }
      })
  }, [connection, domain, isMounted, user?.email])

  return [changePassword, state]
}
